/**
 * Active database context.
 *
 * The user picks a database from the topbar switcher; that selection is
 * threaded into every `/api/tables/...` request via the `?db=<id>`
 * query parameter. The chosen id persists in localStorage so the same
 * DB is selected after a reload.
 *
 * The list of registered databases is loaded once via `/api/databases`
 * and cached in TanStack Query (key: ["databases"]). Mutations
 * elsewhere (the admin Settings page) invalidate that key to refresh
 * the switcher.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import type { ApiClient } from "../api/client";
import type { RegisteredDatabase } from "../api/types";
import { withPendingOp } from "./pending-ops";

const STORAGE_KEY = "kse-active-db-id-v1";

type Ctx = {
  databases: RegisteredDatabase[] | undefined;
  loading: boolean;
  activeId: string | undefined;
  active: RegisteredDatabase | undefined;
  setActiveId: (id: string) => void;
};

const ActiveDatabaseContext = createContext<Ctx>({
  databases: undefined,
  loading: false,
  activeId: undefined,
  active: undefined,
  setActiveId: () => {},
});

export function ActiveDatabaseProvider({
  api,
  children,
}: {
  api: ApiClient;
  children: ReactNode;
}) {
  const dbsQ = useQuery({
    queryKey: ["databases"],
    queryFn: () =>
      withPendingOp("Loading databases…", (signal) =>
        api.get<RegisteredDatabase[]>("/api/databases", { signal }),
      ),
  });

  const [activeId, setActiveIdState] = useState<string | undefined>(
    () => localStorage.getItem(STORAGE_KEY) || undefined,
  );

  // Auto-pick the first enabled database when:
  //   (a) no id is set yet, or
  //   (b) the stored id is no longer in the registry / has been disabled.
  useEffect(() => {
    if (!dbsQ.data) return;
    const stillValid = activeId && dbsQ.data.some((d) => d.id === activeId && d.enabled);
    if (stillValid) return;
    const first = dbsQ.data.find((d) => d.enabled);
    if (first) {
      setActiveIdState(first.id);
      localStorage.setItem(STORAGE_KEY, first.id);
    } else {
      setActiveIdState(undefined);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [dbsQ.data, activeId]);

  const value = useMemo<Ctx>(
    () => ({
      databases: dbsQ.data,
      loading: dbsQ.isLoading,
      activeId,
      active: dbsQ.data?.find((d) => d.id === activeId),
      setActiveId: (id: string) => {
        localStorage.setItem(STORAGE_KEY, id);
        setActiveIdState(id);
      },
    }),
    [dbsQ.data, dbsQ.isLoading, activeId],
  );

  return (
    <ActiveDatabaseContext.Provider value={value}>
      {children}
    </ActiveDatabaseContext.Provider>
  );
}

export function useActiveDb(): Ctx {
  return useContext(ActiveDatabaseContext);
}

/** Build a URL with `?db=<id>` appended (or merged if it already has a query). */
export function withDb(path: string, dbId: string | undefined): string {
  if (!dbId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}db=${encodeURIComponent(dbId)}`;
}
